import { Link } from "wouter";
import { Boxes, Play } from "lucide-react";
import { useStickyState } from "@/lib/useStickyState";

type PlayMode = "virtual" | "live";

// Landing for the Play nav tab. Two options: Virtual Game (3D + physics)
// or Live Game (the real-life logging flow). The last-picked mode is
// remembered (sticky) and floated to the top + highlighted so a repeat
// player lands on their usual mode; a first-time visitor defaults to
// Virtual, the marquee experience.
const MODES: Record<PlayMode, Omit<TileProps, "accent" | "onPick">> = {
  virtual: {
    href: "/dashboard/virtual",
    icon: <Boxes size={24} />,
    title: "Virtual Game",
    subtitle: "3D arena, real physics. Throw bags with your phone.",
  },
  live: {
    href: "/dashboard/games/new",
    icon: <Play size={24} />,
    title: "Live Game",
    subtitle: "Score a real-world game hand-by-hand, with a live spectator link.",
  },
};

export default function PlayChooser() {
  const [lastMode, setLastMode] = useStickyState<PlayMode>(
    "play.lastMode",
    "virtual",
  );
  // Last-used mode first; the other second.
  const order: PlayMode[] =
    lastMode === "live" ? ["live", "virtual"] : ["virtual", "live"];

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
          Play
        </h1>
        <p className="text-muted-foreground text-sm">Pick a mode.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {order.map((mode) => (
          <Tile
            key={mode}
            {...MODES[mode]}
            accent={mode === lastMode}
            onPick={() => setLastMode(mode)}
          />
        ))}
      </div>
    </div>
  );
}

type TileProps = {
  href: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  accent?: boolean;
  onPick: () => void;
};

function Tile({ href, icon, title, subtitle, accent, onPick }: TileProps) {
  return (
    <Link
      href={href}
      onClick={onPick}
      className={`block rounded-lg border p-5 space-y-3 transition-colors ${
        accent
          ? "border-primary bg-primary/5 hover:bg-primary/10"
          : "border-border bg-card hover:bg-muted/50"
      }`}
    >
      <div
        className={`inline-flex items-center justify-center h-12 w-12 rounded-md ${
          accent
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        }`}
      >
        {icon}
      </div>
      <div>
        <h2 className="text-lg font-display uppercase tracking-tight">
          {title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      </div>
    </Link>
  );
}
